import { CommonUtils } from '../(common-lib)/CommonUtils';
import type { LoanDeltaUploadPayload } from '../(common-lib)/loan-patch/types';

export default class StorageClient {
  static async uploadLoanDelta(payload: LoanDeltaUploadPayload): Promise<{
    success: boolean;
    conflict?: boolean;
    status?: number;
    code?: string;
    conflicts?: unknown[];
    currentVersion?: string;
    newVersion?: string;
    error?: string;
  }> {
    try {
      const response = await fetch('/api/storage/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = await StorageClient.readResponseBody(response);
      if (response.status === 409) {
        return {
          success: false,
          conflict: true,
          status: response.status,
          code: body?.code,
          conflicts: body?.conflicts,
          currentVersion: body?.currentVersion,
          newVersion: body?.newVersion || body?.currentVersion,
          error: body?.error || 'Patch conflict',
        };
      }

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          code: body?.code,
          error: body?.error || `Request failed with status ${response.status}`,
        };
      }

      return {
        success: true,
        status: response.status,
        newVersion: body?.newVersion || body?.currentVersion,
      };
    } catch (error) {
      CommonUtils.logError('Error uploading loan delta:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  static async downloadLoanData(loanId: string): Promise<any> {
    try {
      const response = await fetch(`/api/storage/download?formid=${encodeURIComponent(loanId)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store', // This is the crucial change to prevent caching
      });
      
      const payload = await StorageClient.readResponseBody(response);

      if (!response.ok) {
        const error: any = new Error(
          (payload as any)?.error || response.statusText || 'Failed to download loan data'
        );
        error.status = response.status;
        error.payload = payload;
        StorageClient.logDownloadFailure(loanId, error);
        throw error;
      }
      
      if (!payload) {
        const error: any = new Error('Empty response body while downloading loan data');
        error.status = response.status;
        throw error;
      }

      return payload;
    } catch (error) {
      CommonUtils.logError('Error downloading loan data:', { loanId, error });
      throw error;
    }
  }

  private static logDownloadFailure(loanId: string, error: { status?: number; payload?: unknown }) {
    const status = error?.status;
    const details = { loanId, status, errorDetails: error?.payload };
    if (status === 404) {
      CommonUtils.logInfo('No loan data found for loanId', details);
      return;
    }
    if (status === 401 || status === 403) {
      CommonUtils.logWarn('Loan data access denied', details);
      return;
    }
    CommonUtils.logError('Failed to download loan data', details);
  }

  private static async readResponseBody(response: Response): Promise<any> {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      try {
        return await response.json();
      } catch {
        return null;
      }
    }
    try {
      return await response.text();
    } catch {
      return null;
    }
  }

  static async uploadDocument(file, loanData, documentId): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    const uniqueId = documentId || CommonUtils.getUniqueId();
    const rawData = typeof loanData?.getRawLoanData === 'function' ? loanData.getRawLoanData() : loanData;
    const loanId = String(rawData?.crmData?.uniqueLoanId ?? '');
    const folderName = loanId ? `${loanId}/docs` : '';
    const fileType = file.type;

    const res = await fetch('/api/storage/upload-doc', {
      method: 'POST',
      headers: {
        'x-folder-name': folderName,
        'x-document-id': uniqueId,
        'x-file-type': fileType
      },
      body: formData,
    });

    if (!res.ok) {
      throw new Error('Failed to upload file to blob storage');
    }
  }
}
